import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Check, Mail, Shield, Users, Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

// L'Chaim SAA-Aligned Color Palette
const COLORS = {
  darkBlue: '#0a1628',
  mediumBlue: '#1a3a5c',
  cyan: '#00d4ff',
  red: '#dc2626',
  parchment: '#F5F5DC',
  white: '#ffffff',
  gray: '#9ca3af'
};

const TIERS = {
  lchaim_sentry: {
    name: 'The Sentry',
    price: 5,
    icon: Mail,
    description: 'Daily wisdom delivered to your inbox',
    features: ['Daily email delivery', '350 days content', 'Five Pillars curriculum'],
    has_dashboard: false
  },
  lchaim_guardian: {
    name: 'The Guardian',
    price: 19,
    icon: Shield,
    description: 'Full dashboard access',
    features: ['Everything in Sentry', 'Dashboard access', 'Sentry\'s Log', 'Ancestral Vault'],
    has_dashboard: true
  },
  lchaim_order: {
    name: 'The Order',
    price: 24,
    icon: Users,
    description: 'The Tandem Experience',
    features: ['Everything in Guardian', 'Parent-Student linking', 'Coach\'s Companion', 'Real-time sync'],
    has_dashboard: true,
    is_tandem: true
  }
};

// Logo component
const LChaimLogo = () => (
  <span className="text-2xl font-black tracking-tight">
    <span style={{ color: COLORS.white }}>L'CHA</span>
    <span style={{ color: COLORS.cyan }}>IM</span>
  </span>
);

const LChaimEnroll = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTier = searchParams.get('tier');
  const [step, setStep] = useState(initialTier ? 2 : 1);
  const [selectedTier, setSelectedTier] = useState(initialTier || null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    studentName: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTierSelect = (tierId) => {
    setSelectedTier(tierId);
    setStep(2);
  };

  const handleCreateAccount = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/academy/auth/signup', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || ''
      });

      if (res.data.success) {
        localStorage.setItem('academy_token', res.data.token);
        localStorage.setItem('academy_user', JSON.stringify(res.data.user));
        await handleCheckout(res.data.user);
      } else {
        throw new Error(res.data.message || 'Account creation failed');
      }
    } catch (error) {
      // If signup failed because email already exists, try login automatically
      const detail = error.response?.data?.detail || error.response?.data?.message || '';
      if (error.response?.status === 400 && /already|exists/i.test(detail)) {
        try {
          const loginRes = await api.post('/academy/auth/login', {
            email: formData.email,
            password: formData.password,
          });
          if (loginRes.data?.success) {
            localStorage.setItem('academy_token', loginRes.data.token);
            localStorage.setItem('academy_user', JSON.stringify(loginRes.data.user));
            await handleCheckout(loginRes.data.user);
            return;
          }
        } catch (e) {
          toast.error('An account exists with that email. Wrong password?');
        }
      } else {
        toast.error(detail || 'Failed to create account');
      }
      setLoading(false);
    }
  };

  const handleCheckout = async (user) => {
    try {
      const token = localStorage.getItem('academy_token');

      const res = await api.post('/academy/lchaim/checkout', {
        tier: selectedTier,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      toast.error(detail || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const tierInfo = selectedTier ? TIERS[selectedTier] : null;
  const TierIcon = tierInfo?.icon || Shield;

  return (
    <div className="min-h-screen py-12 px-6" style={{ backgroundColor: COLORS.darkBlue }}>
      {/* Grid Background */}
      <div 
        className="fixed inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(${COLORS.cyan} 1px, transparent 1px), linear-gradient(90deg, ${COLORS.cyan} 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <LChaimLogo />
          <p className="mt-2" style={{ color: COLORS.cyan }}>
            Begin Your 350-Day Transformation
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm`}
                style={{ 
                  backgroundColor: step >= s ? COLORS.cyan : COLORS.mediumBlue,
                  color: step >= s ? COLORS.darkBlue : COLORS.gray
                }}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              <span className="text-xs uppercase tracking-wider hidden md:inline" style={{ color: step >= s ? COLORS.white : COLORS.gray }}>
                {s === 1 ? 'Path' : s === 2 ? 'Account' : 'Payment'}
              </span>
              {s < 3 && <div className="w-8 h-0.5" style={{ backgroundColor: COLORS.mediumBlue }} />}
            </div>
          ))}
        </div>

        {/* Step 1: Tier Selection */}
        {step === 1 && (
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(TIERS).map(([tierId, tier]) => {
              const Icon = tier.icon;
              return (
                <div 
                  key={tierId}
                  className="p-5 rounded-lg border cursor-pointer transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: COLORS.mediumBlue,
                    borderColor: tierId === 'lchaim_order' ? COLORS.red : `${COLORS.cyan}40`
                  }}
                  onClick={() => handleTierSelect(tierId)}
                >
                  {tierId === 'lchaim_order' && (
                    <div className="text-xs uppercase tracking-wider text-center mb-3 py-1 rounded" 
                         style={{ backgroundColor: COLORS.red, color: COLORS.white }}>
                      Recommended
                    </div>
                  )}
                  <div className="text-center">
                    <div 
                      className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: COLORS.darkBlue, border: `2px solid ${COLORS.cyan}` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: COLORS.cyan }} />
                    </div>
                    <h3 className="font-black uppercase tracking-wider mb-1" style={{ color: COLORS.white }}>
                      {tier.name}
                    </h3>
                    <p className="text-xs mb-3" style={{ color: COLORS.gray }}>{tier.description}</p>
                    <div className="text-2xl font-black mb-4" style={{ color: COLORS.white }}>
                      ${tier.price}<span className="text-sm font-normal" style={{ color: COLORS.gray }}>/mo</span>
                    </div>
                    <Button 
                      className="w-full font-bold uppercase tracking-wider text-sm"
                      style={{ 
                        backgroundColor: tierId === 'lchaim_order' ? COLORS.red : COLORS.cyan,
                        color: tierId === 'lchaim_order' ? COLORS.white : COLORS.darkBlue
                      }}
                    >
                      Select
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Step 2: Account Creation */}
        {step === 2 && tierInfo && (
          <div className="max-w-lg mx-auto">
            <div className="p-6 rounded-lg border" style={{ backgroundColor: COLORS.mediumBlue, borderColor: `${COLORS.cyan}40` }}>
              {/* Tier Summary */}
              <div className="flex items-center gap-4 mb-6 pb-6" style={{ borderBottom: `1px solid ${COLORS.cyan}30` }}>
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: COLORS.darkBlue, border: `2px solid ${COLORS.cyan}` }}
                >
                  <TierIcon className="w-6 h-6" style={{ color: COLORS.cyan }} />
                </div>
                <div>
                  <h2 className="font-black uppercase tracking-wider" style={{ color: COLORS.white }}>
                    {tierInfo.name}
                  </h2>
                  <p style={{ color: COLORS.cyan }}>${tierInfo.price}/month</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="ml-auto text-xs"
                  style={{ color: COLORS.gray }}
                  onClick={() => setStep(1)}
                >
                  Change
                </Button>
              </div>

              <h3 className="font-bold uppercase tracking-wider mb-4" style={{ color: COLORS.white }}>
                {selectedTier === 'lchaim_order' ? 'Parent Account' : 'Create Account'}
              </h3>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: COLORS.cyan }}>Full Name *</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="mt-1 border-0"
                    style={{ backgroundColor: COLORS.parchment, color: COLORS.darkBlue }}
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: COLORS.cyan }}>Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    className="mt-1 border-0"
                    style={{ backgroundColor: COLORS.parchment, color: COLORS.darkBlue }}
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider" style={{ color: COLORS.cyan }}>Password *</Label>
                  <div className="relative">
                    <Input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Min 6 characters"
                      className="mt-1 border-0 pr-10"
                      style={{ backgroundColor: COLORS.parchment, color: COLORS.darkBlue }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: COLORS.darkBlue }}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {selectedTier === 'lchaim_order' && (
                  <div className="pt-4 mt-4" style={{ borderTop: `1px solid ${COLORS.cyan}30` }}>
                    <p className="text-xs mb-3" style={{ color: COLORS.gray }}>
                      Link your son's account after enrollment
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <Button 
                  variant="outline"
                  onClick={() => setStep(1)}
                  style={{ borderColor: `${COLORS.cyan}50`, color: COLORS.white }}
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <Button 
                  className="flex-1 py-5 font-bold uppercase tracking-wider"
                  style={{ backgroundColor: COLORS.red, color: COLORS.white }}
                  onClick={handleCreateAccount}
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    'Continue to Payment'
                  )}
                </Button>
              </div>

              <p className="text-center text-xs mt-4" style={{ color: COLORS.gray }}>
                Have an account?{' '}
                <button 
                  onClick={() => navigate('/academy/lchaim/login')}
                  className="underline"
                  style={{ color: COLORS.cyan }}
                >
                  Log in
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LChaimEnroll;
